<template>
  <div ref="container" class="basemap" aria-role="application"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { PropType } from 'vue'
import type * as maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { BASEMAP_VIEW_MAX_ZOOM, DEFAULT_MAP_CENTER, DEFAULT_MAPS_BASEMAP_URL, createBasemapStyle } from '../lib/basemapStyle'
import { loadMaplibre } from '../lib/maplibre'
import { schemeMarkerColor } from '../composables/useBasemap'

export type BasemapMarker = {
  id?: string | number
  lat: number
  lng: number
  title?: string
}

const props = defineProps({
  center: {
    type: Object as PropType<{ lat: number, lng: number }>,
    required: false,
    default: () => ({ ...DEFAULT_MAP_CENTER })
  },
  zoom: {
    type: Number,
    required: false,
    default: 13
  },
  markers: {
    type: Array as PropType<BasemapMarker[]>,
    required: false,
    default: () => []
  },
  fitMarkers: {
    type: Boolean,
    required: false,
    default: true
  },
  pick: {
    type: Boolean,
    required: false,
    default: false
  },
  lang: {
    type: String,
    required: false,
    default: 'en'
  },
  tileUrl: {
    type: String,
    required: false,
    default: DEFAULT_MAPS_BASEMAP_URL
  },
  color: {
    type: String,
    required: false
  },
  fullscreen: {
    type: Boolean,
    required: false,
    default: true
  }
})

const emit = defineEmits<{
  pick: [position: { lat: number, lng: number }]
  markerClick: [marker: BasemapMarker]
  markerDrag: [position: { lat: number, lng: number }]
  ready: [map: maplibregl.Map]
}>()

const container = ref<HTMLElement | null>(null)
let maplibre: typeof maplibregl | null = null
let unmounted = false
let map: maplibregl.Map | null = null
let markerInstances: maplibregl.Marker[] = []
let resizeObserver: ResizeObserver | null = null

const syncMarkers = () => {
  if (!map || !maplibre) {
    return
  }
  for (const marker of markerInstances) {
    marker.remove()
  }
  markerInstances = []
  const pinColor = props.color || schemeMarkerColor(container.value)
  for (const item of props.markers) {
    if (!item.lat || !item.lng) {
      continue
    }
    const marker = new maplibre.Marker({
      color: pinColor,
      draggable: props.pick
    })
      .setLngLat([item.lng, item.lat])
      .addTo(map)
    if (item.title) {
      marker.getElement().setAttribute('title', item.title)
      marker.getElement().setAttribute('aria-label', item.title)
    }
    marker.getElement().style.cursor = 'pointer'
    marker.getElement().addEventListener('click', (event) => {
      event.stopPropagation()
      emit('markerClick', item)
    })
    if (props.pick) {
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat()
        emit('markerDrag', { lat: lngLat.lat, lng: lngLat.lng })
      })
    }
    markerInstances.push(marker)
  }
  if (props.fitMarkers && props.markers.length > 1) {
    const bounds = new maplibre.LngLatBounds()
    for (const item of props.markers) {
      bounds.extend([item.lng, item.lat])
    }
    map.fitBounds(bounds, { padding: 48, maxZoom: 13, duration: 0 })
  } else if (props.fitMarkers && props.markers.length === 1) {
    map.jumpTo({
      center: [props.markers[0].lng, props.markers[0].lat],
      zoom: props.zoom
    })
  }
}

const createMap = async () => {
  if (!container.value || map) {
    return
  }
  maplibre = await loadMaplibre()
  if (unmounted || !container.value || map) {
    return
  }
  map = new maplibre.Map({
    container: container.value,
    style: createBasemapStyle({
      tileUrl: props.tileUrl,
      lang: props.lang
    }),
    center: [props.center.lng, props.center.lat],
    zoom: props.zoom,
    maxZoom: BASEMAP_VIEW_MAX_ZOOM,
    attributionControl: {
      compact: true
    }
  })
  map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-right')
  if (props.fullscreen) {
    map.addControl(new maplibre.FullscreenControl(), 'top-right')
  }
  if (props.pick) {
    map.on('click', (event) => {
      emit('pick', { lat: event.lngLat.lat, lng: event.lngLat.lng })
    })
  }
  map.on('load', () => {
    syncMarkers()
    emit('ready', map as maplibregl.Map)
  })
}

onMounted(() => {
  createMap()
  if (container.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      map?.resize()
    })
    resizeObserver.observe(container.value)
  }
})

onBeforeUnmount(() => {
  unmounted = true
  resizeObserver?.disconnect()
  resizeObserver = null
  for (const marker of markerInstances) {
    marker.remove()
  }
  markerInstances = []
  map?.remove()
  map = null
})

watch(() => props.markers, () => {
  syncMarkers()
}, { deep: true })

watch(() => props.color, () => {
  syncMarkers()
})

watch(() => [props.center.lat, props.center.lng, props.zoom], () => {
  if (!map || props.fitMarkers && props.markers.length) {
    return
  }
  map.jumpTo({
    center: [props.center.lng, props.center.lat],
    zoom: props.zoom
  })
})

defineExpose({
  flyTo (options: { lat: number, lng: number, zoom?: number }) {
    map?.flyTo({
      center: [options.lng, options.lat],
      zoom: options.zoom ?? map.getZoom()
    })
  },
  resize () {
    map?.resize()
  }
})
</script>

<style>
.basemap {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
.basemap .maplibregl-map {
  font: inherit;
}
</style>
